import { AxiosError } from "axios";
import { BehaviorSubject } from "rxjs";
import { Call, Device } from "@twilio/voice-sdk";
import { getSessionStorageUser } from "../session/auth.service";
import { Formatter } from "../formatter/formatter.service";
import { CallManagementEvent, CallManagementState } from "../../models/call-management.model";
import { CallerInfo } from "../../models/entities/call.model";
import CallPresenter from "../../models/components/call-presenter.model";
import { EndpointsService } from "../endpoints/endpoints.service";
import { TwilioEndpointsService } from "../endpoints/twilio-endpoints.service";
import {
	DEFAULT_AUDIO_DEVICE_ID,
	getCallCenterPreferences,
} from "../call-center/call-center-preferences.service";

class TwilioVoiceService {
	// State
	state: CallManagementState = {
		calls: [],
		device: undefined,
		initialized: false,
		initializing: false,
	};
	callManagementSubject: BehaviorSubject<CallManagementEvent | undefined> = new BehaviorSubject<
		CallManagementEvent | undefined
	>(undefined);

	initialize = (): void => {
		// Idempotent: the call bar re-mounts on navigation/hot-reload, and every
		// extra Device left registered rings its own copy of each incoming call.
		if (this.state.initialized || this.state.initializing) return;
		this.state.initializing = true;
		this.getAccessToken()
			.then((response: string) => this.registerDevice(response))
			.then(() => {
				// Device is initialized
				this.state.initialized = true;
				// Emit event: Device is Registered
				this.callManagementSubject.next({ callSid: "", callManagementState: this.state });
			})
			.finally(() => (this.state.initializing = false));
	};

	/**
	 * Access token
	 * @description Get a Twilio access token. It's necessary to register the Device.
	 */
	getAccessToken = async (): Promise<string> => {
		return new Promise((resolve, reject) => {
			EndpointsService.twilio
				.getVoiceAccessToken({
					config: {
						params: {
							ttl: 86400, //60 * 60 * 24 Seconds in a day (max ttl)
						},
					},
				})
				.then((response) => {
					console.log("Twilio access token: ", response);
					if (response?.token) resolve(response.token);
					else
						reject(
							new Error(
								`Invalid Twilio access token response: ${JSON.stringify(response)}`
							)
						);
				})
				.catch((error: AxiosError) =>
					reject(error instanceof Error ? error : new Error(String(error)))
				);
		});
	};

	/**
	 * Device: Register
	 * @description Register the Device to the Twilio backend, allowing it to receive calls.
	 */
	registerDevice = async (accessToken: string): Promise<boolean> => {
		return new Promise((resolve, reject) => {
			// Tear down any previous instance so it stops receiving incoming calls
			this.state.device?.destroy();

			// Initialize Device instance
			this.state.device = new Device(accessToken, {
				appName: "gladstone2",
				// Set Opus as our preferred codec. Opus generally performs better, requiring less bandwidth and
				// providing better audio quality in restrained network conditions. Opus will be default in 2.0.
				codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
				allowIncomingWhileBusy: true,
			});

			this.state.device.on("error", (event) => {
				console.log("Device [ERROR]: ", event);
			});

			this.state.device.on("destroyed", (event) => {
				console.log("Device [DESTROYED]: ", event);
			});

			this.state.device.on("registered", (event) => {
				console.log("Device [REGISTERED]: ", event);
			});

			// Listen on incoming calls
			this.state.device.on("incoming", (event: Call) => {
				console.log(
					"1) Incoming call: ",
					event,
					event.parameters["CallSid"],
					event.parameters["From"],
					getSessionStorageUser()?.countryCode,
					getSessionStorageUser()?.phone
				);
				// Guard against duplicate notifications of the same call (e.g. a
				// stale Device instance that is still registered)
				if (this.getCallById(event.parameters["CallSid"])) {
					console.log(
						"Incoming call already tracked, ignoring: ",
						event.parameters["CallSid"]
					);
					return;
				}
				// Create new call
				const newCall = new CallPresenter({
					id: event.parameters["CallSid"],
					call: event,
					fromNumber: event.parameters["From"],
					toNumber: `${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`,
					device: this.state.device,
					updateState: this.updateCallById,
					hangupOtherCalls: this.hangupOtherCalls,
				});
				console.log("2) Incoming call: ", newCall);
				newCall.setCallListeners();
				this.state.calls.push(newCall);

				// Get the participant information
				this.retrieveCallInformation(newCall);

				// Emit event: new call
				this.callManagementSubject.next({
					callSid: newCall.state.id,
					callManagementState: this.state,
				});
			});

			this.state.device.on("offline", () => {
				console.log("Refreshing token due to near expiration");
				this.getAccessToken()
					.then((token) => this.state.device?.updateToken(token))
					.catch(() => (this.state.initialized = false));
			});

			// Apply stored audio preferences (ring sound, microphone, speaker)
			this.applyAudioPreferences();

			// Register the Device
			this.state.device
				.register()
				.then(() => {
					// Device registered OK: ready to recieve incoming calls and make outgoing calls
					console.log("Device regitered: ", this.state.device);
					resolve(true);
				})
				.catch((error: AxiosError) => {
					console.log("Register device - error: ", error);
					reject(new Error("Failed to register device"));
				});
		});
	};

	/**
	 * Audio preferences
	 * @description Push the stored ring-sound / microphone / speaker preferences
	 * into the Device's AudioHelper. Called on Device creation and whenever the
	 * settings page changes a preference. Everything is best-effort: a saved
	 * device that is unplugged or a browser without output selection support
	 * falls back to the system default.
	 */
	applyAudioPreferences = (): void => {
		const audio = this.state.device?.audio;
		if (!audio) return;
		const preferences = getCallCenterPreferences();

		// Incoming ringtone on/off
		audio.incoming(preferences.ringSoundEnabled);

		// Microphone: constrain future call streams instead of setInputDevice(),
		// which would acquire the mic immediately (tab recording indicator).
		if (preferences.inputDeviceId !== DEFAULT_AUDIO_DEVICE_ID) {
			audio
				.setAudioConstraints({ deviceId: preferences.inputDeviceId })
				.catch((error) => console.log("Audio input preference - error: ", error));
		} else {
			audio
				.unsetAudioConstraints()
				.catch((error) => console.log("Audio input preference - error: ", error));
		}

		// Speaker (call audio + ringtone). Only supported in browsers with
		// HTMLMediaElement.setSinkId (Chromium); elsewhere the OS default is used.
		if (audio.isOutputSelectionSupported) {
			audio.speakerDevices
				.set(preferences.outputDeviceId)
				.catch((error) => console.log("Speaker preference - error: ", error));
			audio.ringtoneDevices
				.set(preferences.outputDeviceId)
				.catch((error) => console.log("Ringtone device preference - error: ", error));
		}
	};

	/**
	 * Participant information
	 * @description When receiving an incoming call, map the FROM phone number with our participant's DB.
	 * When making an outbound call, map the TO phone number with our participant's DB.
	 */
	retrieveCallInformation = async (callPresenter: CallPresenter): Promise<void> => {
		const phoneNumber =
			callPresenter.state.call?.direction === Call.CallDirection.Incoming
				? callPresenter.state.fromNumber
				: callPresenter.state.toNumber;
		EndpointsService.dataRetriever
			.getCallInfo({
				config: {
					params: { phoneNumber },
				},
			})
			.then((response: CallerInfo[]) => {
				callPresenter.state.callerInfo = response;
				this.callManagementSubject.next({
					callSid: callPresenter.state.id,
					callManagementState: this.state,
				});
			})
			.catch((error: AxiosError) => {
				console.log("Call: error getting the participants information", error);
			});
	};

	addToConference = async (additionalPhoneNumber: string) => {
		const formattedAdditional =
			Formatter.phoneNumber(additionalPhoneNumber, true) ?? additionalPhoneNumber;

		if (this.state.calls[0].state.isConference) {
			// Already a named conference — add participant directly
			const coachPhone = `${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`;
			await TwilioEndpointsService.addParticipant({
				body: {
					masterPhoneNumber: coachPhone,
					additionalNumber: formattedAdditional,
				},
			});
		} else {
			// Standard call — mutate to conference first
			await TwilioEndpointsService.mutateCallToConferece({
				body: {
					callSid: this.state.calls[0].state.call?.parameters["CallSid"] as string,
					additionalPhoneNumber: formattedAdditional,
					masterPhoneNumber: this.state.calls[0].state.fromNumber,
				},
			});
			this.state.calls[0].state.isConference = true;
		}
	};

	/**
	 * Group Call: initiate a conference from the start
	 * @description Asks the backend to create the participant leg (REST API outbound),
	 * then connects the coach's browser Device directly to the conference room via
	 * device.connect(). This produces a `client:IDENTITY → [empty]` (Incoming) leg
	 * in Twilio's call log — the expected pattern.
	 *
	 * Requires the TwiML Application's Voice URL to be set to
	 * {PUBLIC_GLADSTONE_TWILIO_API}/twilio/public/voice so Twilio returns
	 * <Conference> TwiML when ConferenceName is present.
	 */
	startGroupCall = async (
		participantPhoneNumber: string,
		coachPhoneNumber: string
	): Promise<void> => {
		if (!this.state.device) return;

		const normalise = (num: string) => num.replace(/^\+/, "");
		const conferenceName = `conf-${normalise(participantPhoneNumber)}-${normalise(coachPhoneNumber)}-${Date.now()}`;
		console.log("Starting group call, conference: ", conferenceName);

		const toFormatted = Formatter.phoneNumber(participantPhoneNumber, true) ?? "";

		try {
			// Ask the backend to dial the participant into the conference (REST outbound).
			// Kicked off in parallel but intentionally NOT awaited alongside device.connect():
			// a backend failure (e.g. a 404 on this endpoint) must not stop the coach's call
			// bar from rendering. Otherwise the Device still connects (conference hold music
			// plays) but there is no UI to control or hang up the live call.
			TwilioEndpointsService.startGroupCall({
				body: { conferenceName, participantPhoneNumber, coachPhoneNumber },
			}).catch((error) => {
				console.error("startGroupCall - backend participant leg failed: ", error);
			});

			// Connect the coach's Device to the conference room. The call bar is driven off
			// this resolving, mirroring outboundCall — independent of the backend leg above.
			const call = await this.state.device.connect({
				params: {
					ConferenceName: conferenceName,
					From: `${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`,
					To: toFormatted,
				},
			});

			const newCall = new CallPresenter({
				id: call.outboundConnectionId as string,
				call,
				fromNumber: `${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`,
				toNumber: participantPhoneNumber,
				device: this.state.device,
				updateState: this.updateCallById,
				hangupOtherCalls: this.hangupOtherCalls,
				isConference: true,
			});
			newCall.setCallListeners();
			this.state.calls.push(newCall);
			this.retrieveCallInformation(newCall);
			this.callManagementSubject.next({
				callSid: newCall.state.id,
				callManagementState: this.state,
			});
		} catch (error) {
			console.error("startGroupCall - error: ", error);
		}
	};

	/**
	 * Call: outbound
	 * @description Make an outbound call using the Twilio Device
	 */
	outboundCall = async (phoneNumberTo: string) => {
		if (this.state.device) {
			if (
				this.state.calls.length > 0 &&
				this.state.calls[0].state.call?.status() !== Call.State.Closed
			) {
				this.addToConference(phoneNumberTo);
				return;
			}

			const toFormatted = Formatter.phoneNumber(phoneNumberTo, true) ?? "";
			// Make outbound call using the Device
			this.state.device
				.connect({
					params: {
						To: toFormatted,
						From: `${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`,
						debug: "true",
					},
				})
				.then((event: Call) => {
					// Create new call
					const newCall = new CallPresenter({
						id: event.outboundConnectionId as string, // At this point, the call still doesn't have a CallSid
						call: event,
						fromNumber: `${getSessionStorageUser()?.countryCode}${getSessionStorageUser()?.phone}`,
						toNumber: toFormatted,
						device: this.state.device,
						updateState: this.updateCallById,
						hangupOtherCalls: this.hangupOtherCalls,
					});
					newCall.setCallListeners();
					this.state.calls.push(newCall);

					// Get the participant information
					this.retrieveCallInformation(newCall);

					// Emit event: new call
					this.callManagementSubject.next({
						callSid: newCall.state.id,
						callManagementState: this.state,
					});
				})
				.catch((error: AxiosError) => {
					console.error("connect - error: ", error);
				});
		}
	};

	/**
	 * Hang up current call and immediately start a new outbound call.
	 * Clears the calls array before dialling so outboundCall/startGroupCall
	 * never hit the add-to-conference branch.
	 */
	hangUpAndCall = (phoneNumberTo: string, isGroupCall?: boolean, coachPhone?: string): void => {
		this.state.calls.forEach((c) => c.state.call?.disconnect());
		this.state.calls = [];
		this.callManagementSubject.next({ callSid: "", callManagementState: this.state });
		if (isGroupCall && coachPhone) {
			this.startGroupCall(phoneNumberTo, coachPhone);
		} else {
			this.outboundCall(phoneNumberTo);
		}
	};

	/**
	 * Incoming call: answered
	 * description When a coach answers a new incoming call, hang up on the other possible active calls
	 */
	hangupOtherCalls = (activeCallSid: string): void =>
		this.state.calls.forEach((call) => {
			if (call.state.id !== activeCallSid) call.hangUp();
		});

	getCallById = (id: string): CallPresenter | undefined =>
		this.state.calls.find((call) => call.state.id === id);

	updateCallById = (id: string): void => {
		// Update
		const index: number = this.state.calls.findIndex((call) => call.state.id === id);
		if (this.state.calls[index] && !this.state.calls[index]?.state?.call)
			this.state.calls.splice(index, 1);

		// Emit events
		this.callManagementSubject.next({ callSid: id, callManagementState: this.state });
	};
}

const instance = new TwilioVoiceService();
export default instance;
